import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  useEffect,
} from 'react';
import { Button, Form, Input, Select, TreeSelect, Divider, Collapse, Pagination, Empty, Cascader, Tooltip, Radio, Modal, Row, Col } from 'antd';
import { SearchOutlined, DoubleRightOutlined, DoubleLeftOutlined } from '@ant-design/icons';
import { TabsItemProps } from './type';
import styles from './index.module.less';
import {
  getTreeData,
  filterShowTreeData,
  formatShowTreeData,
} from './utils';
import FilterTree from '../FilterTree';
import { FilterQueryForm } from './DynamicFilterConfig/components/FilterQueryForm';
import { DynamicFilterConfig } from './DynamicFilterConfig';
import SpQiankun from '@/components/sp-qiankun';
import { skuList, dataStatus } from './consts';
import { cloneDeep } from 'lodash';
import { postRecordList, searchMainDataList, getBaseRecordList, getSortList, searchCascade } from '@/api/npi.api';
import { getMainData } from '../FilterTree/service';

const { Option } = Select;
const { Panel } = Collapse;

export const TabsItem: ForwardRefExoticComponent<
  PropsWithoutRef<TabsItemProps> & RefAttributes<Record<any, any>>
> = forwardRef<Record<any, any>, TabsItemProps>(
  (
    {
      sceneType,
      field,
      config,
      columnFrom,
      setRowData,
      customCode,
      onlyCode,
      chooseProduct,
      linkShowModalContrulor,
      onChange,
      setOptionList,
    },
    ref,
  ) => {
    const hideSearchList = field?.bizConfig?.hideSearchList || [];
    const [showTreeData, setShowTreeData] = useState<any>();
    const [treeListInfo, setTreeListInfo] = useState<any[]>([]);
    const [defaultSearch, setDefaultSearch] = useState<any>({});
    const [productTypeList, setProductTypeList] = useState<any[]>([]);
    const [productSortList, setProductSortList] = useState<any[]>([]);
    const [brandType, setBrandType] = useState<any[]>([]);
    const [brandList, setBrandList] = useState<any[]>([]);
    const [filterData, setFilterData] = useState<any>({});
    const tableRef = useRef<any>({});
    const [tableTotalCount, setTableTotalCount] = useState<number>(0);
    const [tableData, setTableData] = useState<any[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');
    const [searchFeatures, setSearchFeatures] = useState<any[]>([]);
    const [cascadeData, setCascadeData] = useState<any[]>([]);
    const [filterDisplay, setFilterDisplay] = useState<boolean>(true);
    const [choosenCode, setChoosenCode] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [radioValue, setRadioValue] = useState('');
    const [routeParams, setRouteParams] = useState({});
    const [isFromEdit, setIsFromEdit] = useState(false);
    const [editRouteUrl, setEditRouteUrl] = useState('');

    const [form] = Form.useForm();
    const productType = Form.useWatch('productType', form);
    const formBrandType = Form.useWatch('brandType', form);
    const formProductSort = Form.useWatch('productSort', form);
    const formBrand = Form.useWatch('brand', form);
    const cascadeVaule = Form.useWatch('cascadeVaule', form);
    const formSku = Form.useWatch('sku', form);
    const managerialStatus = Form.useWatch('managerialStatus', form);

    useImperativeHandle(ref, () => ({}));

    useEffect(() => {
      getCascade().then(() => {
        getProductTypeList()
        getBrandType()
      })
    }, [])

    useEffect(() => {
      tableRef?.current?.resetFilterConfigMap({})
      setSearchFeatures([])
      setFilterData({})
      if (formProductSort) {
        getSearchData()
      }
    }, [formProductSort, formBrandType, formBrand])

    useEffect(() => {
      if (cascadeVaule) {
        getTableList()
      }
    }, [formProductSort, productType, searchValue, searchFeatures, cascadeVaule, formSku, managerialStatus])

    // 获取产品分类
    useEffect(() => {
      if (productType && cascadeVaule) {
        getSortList({
          materialType: cascadeVaule[0],
          businessScope: cascadeVaule[1],
          productType,
        }).then(res => {
          setProductSortList(res)
        })
      }
    }, [productType, cascadeVaule])

    // 获取品牌
    useEffect(() => {
      if (formBrandType) {
        getBaseRecordList({
          model_id: 'bd_brand_classification',
          data: {
            condition: 'brand_class = ${brand_class}',
            parameters: { brand_class: formBrandType },
            pageSize: 1000,
          },
        }).then(([_, result]) => {
          const { data = {} } = result || {};
          const targetOptions = (data?.data || []).map((dataItem: any) => ({
            ...dataItem,
            label: dataItem.name,
            value: dataItem.number
          }));
          setBrandList(targetOptions)
        })
      }
    }, [formBrandType])

    const getCascade = async () => {
      const params = {
        businessDomainType: 'scope_mdm',
        useSceneCode: sceneType,
        codes: 'material_type',
      };
      const { defaultVaule, res } = await searchCascade(params);
      setCascadeData(res);
      if (config?.value && res?.[0]?.children?.some((item: any) => item.value === 'customer')) {
        form.setFieldValue('cascadeVaule', ['P', 'customer']);
        setTimeout(() => {
          setSearchValue(config?.value);
        }, 200);
        searchMainDataList({
          materialType: 'P',
          businessDomain: 'customer',
          searchValue: config?.value,
        }).then((list: any) => {
          setRadioValue(config?.value);
          const data = list?.list?.find(listItem => listItem.code === config?.value);
          setChoosenCode(data);
          chooseProduct(data, 'customer');
        });
      } else {
        form.setFieldValue('cascadeVaule', defaultVaule);
      }
    };

    // 获取产品品类
    const getProductTypeList = async () => {
      const [_, result] = await getBaseRecordList({
        model_id: 'bd_product_type',
        data: {
          pageSize: 1000,
        },
      });
      const { data = {} } = result || {};
      const targetOptions = (data?.data || []).map((dataItem: any) => ({
        ...dataItem,
        label: dataItem.name,
        value: dataItem.number
      }));
      setProductTypeList(targetOptions)
      // form.setFieldValue('productType', targetOptions?.[0]?.value)
    };

    // 获取品牌类型
    const getBrandType = async () => {
      const [_, result] = await getBaseRecordList({
        model_id: 'bd_brand_classification_type',
        data: {
          pageSize: 1000,
        },
      });
      const { data = {} } = result || {};
      const targetOptions = (data?.data || []).map((dataItem: any) => ({
        ...dataItem,
        label: dataItem.name,
        value: dataItem.code
      }));
      setBrandType(targetOptions)
    };

    const getFilterData = (data) => {
      setFilterData(data)
    }

    const getTableList = (pageNo?: number) => {
      const params = {
        materialType: cascadeVaule[0],
        businessDomain: cascadeVaule[1],
        searchValue,
        managerialStatus: managerialStatus ?? '02',
        sku: formSku === undefined ? null : formSku,
        classificationNumber: formProductSort,
        features: searchFeatures,
        pageNo: pageNo || 1,
        pageSize: 5,
        productType,
      };
      // 客户产品增加客户经营组织和编号过滤
      if (cascadeVaule?.[1] === 'customer') {
        // if (field?.bizConfig?.onlyCodeType === 'ltc_contract_basic_info_id') {
        //   params.contractBasicInfoId = onlyCode || '';
        // }
        // if (field?.bizConfig?.onlyCodeType === 'order_no') {
        //   params.orderNo = onlyCode || '';
        // }
        // if (field?.bizConfig?.onlyCodeType === 'project_no') {
        //   params.projectNo = onlyCode || '';
        // }
        params.customerNumber = customCode || '';
      }
      searchMainDataList(params).then(res => {
        setTableTotalCount(res?.totalCount || 0)
        setTableData(res?.list || [])
      })
    }

    // 获取过滤树
    const getSearchData = async () => {
      const res = await getMainData({
        productCode: formProductSort,
        brandType: formBrandType,
        brandCode: formBrand,
      });
      if (res) {
        setShowTreeData(res?.treeData);
        // getShowTreeData(res, formProductSort);
      } else {
        setShowTreeData(undefined);
      }
    };

    const getShowTreeData = async (mainData: any, productCode: string) => {
      const productRes = await postRecordList({
        app_id: 'base',
        model_id: 'bd_bpart_product_classification',
        number: productCode,
      });
      const newProductName = productRes?.data[0]?.name;
      const number = mainData?.number;
      const listRes = await postRecordList({
        app_id: 'master_data',
        model_id: 'md_bbom_template_line',
        md_bbom_template_number: number,
      });
      const list = listRes.data || [];
      setTreeListInfo(list);
      const treeRes = await postRecordList({
        app_id: 'base',
        model_id: 'bd_material_classification',
      });
      const allList = treeRes.data.map((el: any) => {
        const idItem = list.find(
          (item: any) => item.bd_material_classification_number === el.number,
        );
        return {
          ...(idItem || {}),
          ...el,
          key: el.number,
          title: el.name,
        };
      });
      const newTreeData = getTreeData(allList, 'superior_number', 'number');
      const allData = {
        title: newProductName,
        key: productCode,
        children: newTreeData,
      };
      const keys = list.map((el: any) => el.bd_material_classification_number);
      const newData = filterShowTreeData(allData, keys);
      const newShowData = formatShowTreeData(newData);
      setShowTreeData(newShowData);
    };

    const handlePreFilterChange = (val: any, preFilter: any) => {
      if (Object.keys(preFilter)?.length > 0) {
        const features = []
        for (const key in preFilter) {
          if ({}.hasOwnProperty.call(preFilter, key) && preFilter[key]) {
            const filterMap = JSON.parse(preFilter[key])
            features.push({
              code: filterMap.feature_code,
              value: filterMap.value,
              featureType: filterMap.featureType,
              materialClassification: filterMap.materialClassification
            })
          }
        }
        setSearchFeatures(features)
      } else {
        setSearchFeatures([])
      }
    };

    const changePageSize = (current, size) => {
      getTableList(current)
    }

    const resetAll = () => {
      const initCascade = cloneDeep(cascadeVaule);
      tableRef?.current?.resetFilterConfigMap({});
      setSearchFeatures([]);
      setFilterData({});
      setSearchValue('');
      form.resetFields();
      form.setFieldValue('cascadeVaule', initCascade);
    };

    const jumpCpart = (item: any, type: string, isEdit = false) => {
      setIsFromEdit(isEdit);
      let caprtInfo: Record<any, any> = {};
      if (type === 'sale') {
        caprtInfo.product_name = item.name; // 客户产品名称
        caprtInfo.spu = item.code; // 销售产品
        caprtInfo.product_type = item.product_type; // 产品品类
        caprtInfo.bd_bpart_product_classification_number = item.bd_bpart_product_classification_number; // 设计分类
        caprtInfo.bd_brand_classification_type_code = item.bd_brand_classification_type_code; // 品牌类型
        caprtInfo.bd_brand_classification_number = item.product_brand; // 品牌
        caprtInfo.audit_status = '03';
        caprtInfo.managerial_status = '02';
        caprtInfo.product_desc = item.desc;
        caprtInfo.sale_basic_price = item.sale_basic_price;
        caprtInfo.unit_code = item.unit_code;
        caprtInfo.product_model = item.product_model;
        caprtInfo.cus_org_code = customCode; // 客户经营组织编码
        caprtInfo[field?.bizConfig?.onlyCodeType] = onlyCode; // 唯一编号
      }
      if (type === 'customer') {
        caprtInfo = cloneDeep(item);
        caprtInfo.product_name = item.name;
        caprtInfo.product_desc = item.desc;
        caprtInfo.cus_org_code = customCode;
      }
      if (isEdit) {
        setEditRouteUrl(`/lcp/task/TASK20221209134843676460/edit/${item.id}`);
      }
      setRouteParams(caprtInfo);
      setIsModalOpen(true);
    };

    const clickRadio = (value: any) => {
      // setFilterDisplay(false);
      // setChoosenCode(e.target.value);
      setRadioValue(value);
      const data = tableData.find(item => item.code === value);
      setChoosenCode(data);
      chooseProduct(data, cascadeVaule?.[1]);
    };

    const saveCb = (tag: boolean, result: any, values: any) => {
      if (tag) {
        if (isFromEdit) {
          onChange(values?.record?.[0]?.values?.product_code);
          setOptionList([{
            name: values?.record?.[0]?.values?.product_name,
            code: values?.record?.[0]?.values?.product_code,
          }]);
        } else {
          onChange(result?.data?.queryData?.record?.[0]?.value?.product_code);
          setOptionList([{
            name: result?.data?.queryData?.record?.[0]?.value?.product_name,
            code: result?.data?.queryData?.record?.[0]?.value?.product_code,
          }]);
        }
        setIsModalOpen(false);
        linkShowModalContrulor(false);
      }
    };

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Form form={form} layout="inline">
            <Row gutter={[0, 10]}>
              {
                hideSearchList.includes('cascadeVaule') ? '' :
                  <Form.Item name="cascadeVaule" label="" >
                    <Cascader
                      style={{ width: '150px' }}
                      options={cascadeData}
                      placeholder="过滤域"
                      allowClear={false}
                      popupClassName={styles.product_filter_content}
                      displayRender={(label, selectedOptions) => {
                        return label?.[1] || '';
                      }}
                    />
                  </Form.Item>
              }
              {
                hideSearchList.includes('productType') ? '' :
                  <Form.Item name="productType" label="" >
                    <Select
                      style={{ width: '150px' }}
                      placeholder="产品品类"
                      onChange={() => {
                        form.setFieldValue('productSort', null)
                      }}
                      allowClear
                    >
                      {
                        productTypeList.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)
                      }
                    </Select>
                  </Form.Item>
              }
              {
                (cascadeVaule?.[1] !== 'customer' && !hideSearchList.includes('productSort')) ?
                  <Form.Item name="productSort" label="" >
                    <TreeSelect
                      style={{ width: '150px' }}
                      placeholder="产品分类"
                      allowClear
                      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                      treeData={productSortList}
                      fieldNames={{ label: 'name', value: 'number' }}
                    />
                  </Form.Item> : ''
              }
              {
                (cascadeVaule?.[1] === 'sale' && !hideSearchList.includes('sku')) ?
                  <Form.Item name="sku" label="" >
                    <Select
                      style={{ width: '150px' }}
                      defaultValue={null}
                      placeholder="产品类型"
                    >
                      {
                        skuList.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)
                      }
                    </Select>
                  </Form.Item> : ''
              }
              {
                hideSearchList.includes('managerialStatus') ? '' :
                  <Form.Item name="managerialStatus" label="" >
                    <Select
                      style={{ width: '150px' }}
                      defaultValue="02"
                      placeholder="数据管理状态"
                    >
                      {
                        dataStatus.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)
                      }
                    </Select>
                  </Form.Item>
              }
              {
                hideSearchList.includes('brandType') ? '' :
                  <Form.Item name="brandType" label="" >
                    <Select
                      style={{ width: '150px' }}
                      placeholder="品牌类型"
                      onChange={() => {
                        form.setFieldValue('brand', null)
                      }}
                      allowClear
                    >
                      {
                        brandType.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)
                      }
                    </Select>
                  </Form.Item>
              }
              {
                hideSearchList.includes('brand') ? '' :
                  <Form.Item name="brand" label="" >
                    <Select
                      style={{ width: '150px' }}
                      placeholder="品牌"
                      allowClear
                    >
                      {
                        brandList.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)
                      }
                    </Select>
                  </Form.Item>
              }
            </Row>
          </Form>
          {
            cascadeVaule?.[1] === 'customer' ?
              <Button
                type="primary"
                onClick={() => {
                  // linkShowModalContrulor(false);
                  setRouteParams({ cus_org_code: customCode });
                  setIsModalOpen(true);
                }}
              >
                新增
              </Button> : ''
          }
        </div>
        <Divider style={{ margin: '8px 0' }} />
        <div className={styles.modal_content}>
          <div className={filterDisplay ? styles.modal_content_left : styles.hide_content}>
            <div className={styles.filter_btn_content}>
              <Button className={styles.reset_btn} onClick={resetAll}>
                重置
              </Button>
              <DynamicFilterConfig
                configRef={tableRef}
                defaultSearch={defaultSearch}
                treeData={showTreeData}
                onFilter={getFilterData}
                productType={productType}
              />
            </div>
            {
              Object.keys(filterData)?.length > 0 ?
                <Collapse>
                  {
                    Object.keys(filterData).map(key => {
                      return (
                        <Panel header={filterData[key].keyNode.label} key={filterData[key].keyNode.id}>
                          <FilterQueryForm configMap={filterData[key]} onChange={handlePreFilterChange} />
                        </Panel>
                      );
                    })
                  }
                </Collapse> : ''
            }
          </div>
          <Button
            className={styles.icon_btn_content}
            shape="circle"
            onClick={() => {
              setFilterDisplay(!filterDisplay);
            }}
            icon={filterDisplay ? <DoubleLeftOutlined /> : <DoubleRightOutlined />}
          />
          <div className={filterDisplay ? styles.modal_content_right : styles.modal_content_left_table}>
            <div className={styles.filter_search_content}>
              <Input style={{ width: '388px' }} value={searchValue} placeholder="输入关键词搜索" prefix={<SearchOutlined />} onChange={(e) => { setSearchValue(e.target.value) }} />
            </div>
            <div className={styles.table_content}>
              <Radio.Group value={radioValue} onChange={(e) => clickRadio(e.target.value)} style={{ width: '100%' }}>
                {
                  tableData.map(item => {
                    return (
                      <div onClick={() => clickRadio(item.code)} className={styles.table_line_content}>
                        <div className={styles.table_line_column_content}>
                          <Radio value={item.code} />
                          <img style={{ width: '60px', height: '60px' }} src={item.picture || '/static/images/default.webp'} />
                          <div className={styles.table_line_title_content}>
                            <Tooltip title={item.name}>
                              <span>{item.name}</span>
                            </Tooltip>
                            <Tooltip title={item.code}>
                              <span>产品编码：{item.code}</span>
                            </Tooltip>
                          </div>
                        </div>
                        {
                          cascadeVaule?.[1] === 'sale' ?
                            <div style={!filterDisplay ? { display: 'none' } : {}}>
                              <Button
                                type="link"
                                onClick={() => {
                                  jumpCpart(item, 'sale');
                                }}
                              >
                                定制
                              </Button>
                            </div> : ''
                        }
                        {
                          cascadeVaule?.[1] === 'customer' ?
                            <div style={!filterDisplay ? { display: 'none' } : {}}>
                              <Button
                                type="link"
                                onClick={() => {
                                  jumpCpart(item, 'customer');
                                }}
                              >
                                复制
                              </Button>
                              <Button
                                type="link"
                                onClick={() => {
                                  jumpCpart(item, 'customer', true);
                                }}
                              >
                                编辑
                              </Button>
                            </div> : ''
                        }
                      </div>
                    );
                  })
                }
              </Radio.Group>
              {
                tableTotalCount > 0 ?
                  <div className={styles.table_pagination}>
                    <span style={!filterDisplay ? { display: 'none' } : {}} className={styles.total_font}>共 {tableTotalCount} 条</span>
                    <Pagination showLessItems size="small" onChange={changePageSize} pageSize={5} total={tableTotalCount} showSizeChanger={false} />
                  </div> :
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
              }
            </div>
          </div>
          <div className={filterDisplay ? styles.hide_content : styles.modal_content_right}>
            {
              FilterTree({
                choosenCode,
                formProductSort,
                formBrandType,
                formBrand,
                setFilterDisplay,
                field,
                config,
                columnFrom,
                setRowData,
                onlyCode,
                customCode,
                setOptionList,
                onChange,
                linkShowModalContrulor,
                nodeType: cascadeVaule?.[1],
              })
            }
          </div>
        </div>
        {isModalOpen &&
          <Modal
            title={isFromEdit ? '编辑' : '新增'}
            visible
            onCancel={() => setIsModalOpen(false)}
            width="70vw"
            footer={null}
            destroyOnClose
            maskClosable={false}
          >
            <SpQiankun
              saveCb={saveCb}
              modal
              routeUrl={isFromEdit ? editRouteUrl : `/lcp/task/TASK20221209134843676460/create?initParams=${JSON.stringify({ md_cpart_product_base_info: routeParams })}`}
            />
          </Modal>
        }
      </div>
    );
  },
);

export default TabsItem;
